<script>
    import tagService from "./service.js";
    import {
        assets,
        searchAssets,
        cancelAssetSearch,
    } from "../asset/assets.state.svelte.js";
    import {
        findings,
        searchFindings,
        cancelFindingSearch,
    } from "../finding/findings.state.svelte.js";
    import {
        vulnerabilities,
        searchVulnerabilities,
        cancelVulnerabilitySearch,
        getVulnerabilityDetail,
    } from "../vulnerability/vulnerabilities.state.svelte.js";
    import BreadCrumNav from "../../components/breadcrum-nav.svelte";
    import List from "./_components/list.svelte";
    import { format } from "svelte-i18n";
    import * as utils from "../../js/utils.js";
    import { onMount, onDestroy } from "svelte";

    let loading = $state(false);

    const data = $state({
        data: [],
        associations: {},
    });

    const updateLoadingStatus = () => {
        loading = !loading;
    };

    const sortTagsAssets = (tags) => {
        for (let tag of tags) {
            if (tag.asset.length > 0) {
                tag.asset = utils.sortAllAssets(tag.asset);
            }
        }
        return tags;
    };

    const hydrateTagsWithAssets = async (tags, associations) => {
        for (let tag of tags) {
            tag.asset = [];
            for (let association of associations.assetAssociations) {
                if (tag.id === association.tag_id) {
                    const asset = assets.data.find(
                        (a) => a.id === association.target_object_id,
                    );
                    if (asset) tag.asset.push(asset);
                }
            }
        }
        return sortTagsAssets(tags);
    };

    const hydrateTagsWithFindings = async (tags, associations) => {
        for (let tag of tags) {
            tag.finding = [];
            for (let association of associations.findingAssociations) {
                if (tag.id === association.tag_id) {
                    const finding = findings.list.data.find(
                        (f) => f.id === association.target_object_id,
                    );
                    if (finding) tag.finding.push(finding);
                }
            }
        }
        return tags;
    };

    const hydrateTagsWithVulnerabilities = async (tags, associations) => {
        if (vulnerabilities.data.length < 1) {
            await searchVulnerabilities("v2/vulnerability/", {});
        }

        for (let tag of tags) {
            tag.vulnerability = [];
            for (let association of associations.vulnerabilityAssociations) {
                if (tag.id === association.tag_id) {
                    let vulnerability =
                        vulnerabilities.data.find(
                            (v) => v.id === association.target_object_id,
                        ) ??
                        (await getVulnerabilityDetail(
                            association.target_object_id,
                            {},
                        ));

                    if (vulnerability) tag.vulnerability.push(vulnerability);
                }
            }
        }
        return tags;
    };

    const updateHydrationData = async () => {
        await searchFindings("v2/finding/", { buildList: {tags: {elements: data.data, associations: data.associations}} });
        if (!assets.loading)
            updateTags(
                await hydrateTagsWithAssets(data.data, data.associations),
            );
        if (!findings.loading)
            updateTags(
                await hydrateTagsWithFindings(data.data, data.associations),
            );
        if (!vulnerabilities.loading)
            updateTags(
                await hydrateTagsWithVulnerabilities(
                    data.data,
                    data.associations,
                ),
            );
    };

    const getTagsAndAssociations = async () => {
        updateLoadingStatus();

        const tags = await tagService.search("v2/tag/", {});
        const tagAssociations = await tagService.search(
            "v2/tag/association/",
            {},
        );

        tagAssociations.assetAssociations = tagAssociations.filter(
            (a) => a.target_object_type === "Asset",
        );
        tagAssociations.findingAssociations = tagAssociations.filter(
            (a) => a.target_object_type === "Finding",
        );
        tagAssociations.vulnerabilityAssociations = tagAssociations.filter(
            (a) => a.target_object_type === "Vulnerability",
        );

        data.associations = tagAssociations;
        updateTags(tags);

        updateLoadingStatus();
        await updateHydrationData();
    };

    const updateTags = (updatedTags = []) => {
        if (!updatedTags.length) return;

        const index = new Map(data.data.map((a, i) => [a.id, i]));

        for (const updated of updatedTags) {
            const idx = index.get(updated.id);

            if (idx !== undefined) {
                data.data[idx] = {
                    ...data.data[idx],
                    ...updated,
                };
            }
            else {
                updated.asset = [];
                updated.finding = [];
                updated.vulnerability = [];
                data.data.unshift(updated);
            }
        }
    };

    const handleDelete = (event) => {
        const ids = event.detail.ids;
        data.data = data.data.filter((tag) => !ids.includes(tag.id));
    };

    onMount(() => {
        getTagsAndAssociations();
    });

    onDestroy(() => {
        cancelAssetSearch();
        cancelFindingSearch();
        cancelVulnerabilitySearch();
    });
</script>

<section class="content-header">
    <div class="container-fluid">
        <div class="row">
            <div class="col-6">
                <h1 class="mt-3 mb-3 page-title">
                    <i class="fas fa-tags fa-xs"></i>
                    {$format("layout.sidebar.tags")}
                </h1>
            </div>
            <div class="col-6">
                <BreadCrumNav
                    routes={[{ label: $format("layout.sidebar.tags") }]}
                />
            </div>
        </div>
    </div>
</section>

<section class="content">
    <div class="container-fluid">
        {#if loading}
            <div class="overlay-wrapper">
                <div class="text-center" class:overlay={loading}>
                    <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                </div>
            </div>
        {/if}
        <List
            data={data.data}
            {loading}
            {getTagsAndAssociations}
            {updateTags}
            on:del={handleDelete}
        />
    </div>
</section>
